import { useUncontrolledError } from "../../utils/useUncontrolledError";
import MappingFieldList from "./MappingFieldList";
import MappingValueInput from "./MappingValueInput";
import MappingValueList from "./MappingValueList";
import EnhancedLinearTagSelector from "./merge/EnhancedLinearTagSelector";
import EnhancedMergeUserMappingInput from "./merge/EnhancedMergeUserMappingInput";
import { switchMappingType } from "./switchMappingType";
import EnhancedZendeskMappingInput from "./zendesk/EnhancedZendeskMappingInput";
import { getBuiltInFields, getBuiltInFieldsClientMapping, getFullFieldName, LIST_ITEM_FIELD_NAME, mappingValueIsValid } from "@avenueops/frontend-shared";
import {
  ClientMappingApiType,
  ClientMappingField,
  ClientMappingValue,
  ENHANCED_MERGE_USER_FIELDS,
  ENHANCED_ZENDESK_FIELDS,
  MERGE_NOTIFICATION_TYPES,
  MERGE_TAGS_LIST,
} from "@avenueops/shared-types";
import { Button } from "@avenueops/web-components";
import _ from "lodash";
import React, { useEffect } from "react";

interface Props {
  selectedIntegrationId: string;
  integrationData?: any;
  update: (_: ClientMappingValue) => void;
  remove: () => void;
  value: ClientMappingValue;
  mappingPrefix: string;
  clientMappingApiType: ClientMappingApiType;
  isFirst: boolean;
  level: number;
  prefix: string;
}

export default function MappingValue({
  selectedIntegrationId,
  integrationData,
  update,
  value: item,
  remove,
  mappingPrefix,
  clientMappingApiType,
  isFirst,
  level,
  prefix,
}: Props) {
  const fullFieldName = getFullFieldName(mappingPrefix, LIST_ITEM_FIELD_NAME);
  const options = getBuiltInFields(mappingPrefix, clientMappingApiType, "OPTIONAL");

  // if the field name is in options just get the type of that option and
  // don't allow the user to change it
  const itemType = options.get(LIST_ITEM_FIELD_NAME) ? options?.get(LIST_ITEM_FIELD_NAME)?.type : item.type;

  // This ensures that if the user has created nested objects w/o names, we won't embed the first layer's required fields in it
  const requiredFieldsPrefix = mappingPrefix === "" && level > 0 ? "_" : "";
  const requiredFieldsMapping = getBuiltInFieldsClientMapping(`${requiredFieldsPrefix}${fullFieldName}`, clientMappingApiType, "REQUIRED");
  useEffect(() => {
    // Set fieldType if it's different from the current value
    if (itemType != item.type) {
      update({ ...item, type: itemType } as ClientMappingValue);
    }
    if (item.type === "OBJECT" && item.fields === undefined) {
      update({ ...item, fields: requiredFieldsMapping.fields } as ClientMappingValue);
    }
  }, [item]);

  const error = useUncontrolledError<ClientMappingValue>(mappingValueIsValid, item);

  return (
    <div data-prefix={`${prefix}`}>
      <div className="flex flex-row w-full pb-5">
        <div className="pt-6">
          <Button type="SECONDARY" disabled={options.has(LIST_ITEM_FIELD_NAME)} onClick={() => switchMappingType<ClientMappingValue>(update, item)}>
            {item.type === "VALUE" ? "=" : item.type === "LIST" ? "[]" : "{}"}
          </Button>
        </div>
        {item.type === "VALUE" && (
          <div className="flex-grow pl-5">
            {clientMappingApiType === "ZENDESK" && ENHANCED_ZENDESK_FIELDS.has(fullFieldName) ? (
              <EnhancedZendeskMappingInput
                zendeskIntegrationId={selectedIntegrationId}
                fieldError={error}
                fullFieldName={fullFieldName}
                fieldValue={item.value}
                onChange={(newValue: string) => update({ ...item, value: newValue })}
                mappingPrefix={mappingPrefix}
                isFirst={isFirst}
              />
            ) : MERGE_NOTIFICATION_TYPES.has(clientMappingApiType) && ENHANCED_MERGE_USER_FIELDS.has(fullFieldName) && integrationData ? (
              <div className="pl-5">
                <EnhancedMergeUserMappingInput
                  integrationId={selectedIntegrationId}
                  integrationData={integrationData}
                  fieldError={error}
                  fieldValue={item.value}
                  onChange={(newValue: string) => update({ ...item, value: newValue })}
                  isFirst={isFirst}
                />
              </div>
            ) : clientMappingApiType === "LINEAR" && fullFieldName === MERGE_TAGS_LIST && integrationData ? (
              <div className="pl-5">
                <EnhancedLinearTagSelector
                  integrationId={selectedIntegrationId}
                  fieldValue={item.value}
                  fieldError={error}
                  onChange={(newValue: string) => update({ ...item, value: newValue })}
                />
              </div>
            ) : (
              <div className="w-auto">
                <MappingValueInput update={update} isFirst={isFirst} prefix={prefix} item={item} />
              </div>
            )}
          </div>
        )}
        <div className="pt-6 pl-5">
          <Button onClick={remove} type="DANGER_SECONDARY" disabled={false}>
            -
          </Button>
        </div>
      </div>
      {item.type === "OBJECT" && (
        <div className="flex flex-row">
          <div className="border-l border-cool-gray-300 pr-5 mb-5" />
          <MappingFieldList
            integrationData={integrationData}
            selectedIntegrationId={selectedIntegrationId}
            isFirst={false}
            prefix={`${prefix}.fields`}
            mappingPrefix={fullFieldName}
            clientMappingApiType={clientMappingApiType}
            level={level + 1}
            fields={item.fields}
            update={(fields: ClientMappingField[]) => update({ ...item, fields })}
          />
        </div>
      )}
      {item.type === "LIST" && (
        <div className="flex flex-row">
          <div className="border-l border-cool-gray-300 pr-5 mb-5" />
          <MappingValueList
            integrationData={integrationData}
            selectedIntegrationId={selectedIntegrationId}
            isFirst={false}
            prefix={`${prefix}.values`}
            mappingPrefix={fullFieldName}
            clientMappingApiType={clientMappingApiType}
            level={level + 1}
            values={item.values}
            update={(values: ClientMappingValue[]) => update({ ...item, values })}
          />
        </div>
      )}
    </div>
  );
}
